// UserService — migrated to the OpenAPI-generated client.
//   - user/me         -> userControllerGetCurrentUser (HttpResponse<User> envelope; caller reads res.data.body)
//   - user (POST)     -> userControllerUpdateUser
//   - system/company/{companyId}/users -> systemCompanyControllerGetCompanyUsers (SYSTEM_ADMIN pool)
// Each function keeps its old return shape; the adapter returns `{data}` on success and re-throws an
// axios-shaped `{response:{data,status}}` on error. Auth/timezone headers come from configureApi's
// interceptor, so the legacy `token` arg on getUser is accepted for signature compatibility but ignored.

import {
  userControllerGetCurrentUser,
  userControllerUpdateUser,
  systemCompanyControllerGetCompanyUsers,
  adminUserControllerSearch,
} from '../api/generated';

// Preserve the legacy axios rejection shape so call sites reading `e.response.data.errorCode` keep working.
const throwLikeAxios = (error, response) => {
  throw {response: {data: error, status: response?.status}};
};

// When a `token` is passed, send it explicitly as the Authorization header. This is REQUIRED by the
// login flow (Login.js): it calls getUser(token) to fetch the profile BEFORE it stores the token in
// localStorage. If we relied on the configureApi interceptor (which reads localStorage['auth']), a
// fresh/incognito session has no token there yet, so /user/me would go out unauthenticated -> 401 ->
// login fails. Passing the token here makes getUser self-sufficient. The interceptor only fills in
// Authorization when it isn't already set, so this override wins; callers that pass no token fall
// back to the interceptor's localStorage token (e.g. impersonation, which swaps localStorage first).
export async function getUser(token) {
  const options = token ? {headers: {Authorization: `Bearer ${token}`}} : undefined;
  const {data, error, response} = await userControllerGetCurrentUser(options);
  if (error) throwLikeAxios(error, response);
  return {data};
}

export async function getUserLoggedIn() {
  const {data, error, response} = await userControllerGetCurrentUser();
  if (error) throwLikeAxios(error, response);
  return {data};
}

export async function updateUser(user) {
  const {data, error, response} = await userControllerUpdateUser({body: user});
  if (error) throwLikeAxios(error, response);
  return {data};
}

// Candidate users of the CALLER's company that do NOT already hold `role` — the pool the promote
// dialogs pick from (e.g. "קידום משתמש קיים למאמן").
//
// Backed by AdminUserController (GET /admin/user), gated ADMIN|TRAINER and company-scoped from the
// principal. It used to call SystemCompanyController (/system/company/{id}/users), which is
// SYSTEM_ADMIN|COMPANY_GROUP_ADMIN only — a plain company ADMIN got a 403, the caller's empty
// .catch swallowed it, and the picker rendered "לא נמצאו משתמשים". That read to users as "the
// search can't find my admin user".
//
// `companyId` is accepted for signature compatibility but intentionally NOT sent: the server
// derives the company from the principal, never a request param (IDOR guard).
//
// `role` takes one role or several. Several is what the manager picker needs: it wants users who
// are neither ADMIN nor MANAGER, and asking for non-ADMINs alone returned the company's existing
// managers — whom createManager then refused for already holding the role.
export async function getUsersWithoutRoles(role, companyId) {
  const {data, error, response} = await adminUserControllerSearch({
    // The generated type is an array now; a single role is normalised rather than pushed onto every
    // call site, since most callers grant exactly one.
    query: {withoutRole: Array.isArray(role) ? role : [role]},
  });
  if (error) throwLikeAxios(error, response);
  return {data};
}

// SYSTEM_ADMIN-only variant: pick candidates in an ARBITRARY company (the super-admin flows that
// choose a company first). Company ADMINs cannot call this — use getUsersWithoutRoles instead.
export async function getCompanyUsersWithoutRoles(role, companyId) {
  const {data, error, response} = await systemCompanyControllerGetCompanyUsers({
    path: {id: companyId},
    query: {withoutRole: role},
  });
  if (error) throwLikeAxios(error, response);
  return {data};
}
